// Org Fleet audit log — reverse-chrono view of pledge events. Server caps the table at
// 1000 rows (oldest pruned on insert), so no pagination is needed in V1.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Search, ChevronRight, Flag } from 'lucide-react';
import api from '../api/axios';
import styles from '../styles/UserFleet.module.css';

interface AuditEntry {
  id: number;
  timestamp: string;
  actorUserId: number;
  actorUsername?: string;
  userFleetId?: number;
  shipName: string;
  manufacturer: string;
  action: string;
  context?: string;
}

const ACTION_LABELS: Record<string, string> = {
  'Pledged': '🚩 Pledged',
  'Unpledged': '↩️ Unpledged',
  'AutoUnpledged-ShipDelete': '🗑️ Auto: Ship Deleted',
  'AutoUnpledged-FleetDelete': '💥 Auto: Fleet Deleted',
  'AutoUnpledged-StatusChange': '🔄 Auto: Status Change',
};

const OrgFleetAudit: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/org/fleet/audit');
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError("You don't have permission to view the audit log. This page is restricted to Admin and Leader roles.");
      } else {
        setError(err?.response?.data?.message || err.message || 'Failed to load audit log');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let result = [...entries];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(e =>
        e.shipName.toLowerCase().includes(q) ||
        e.manufacturer.toLowerCase().includes(q) ||
        (e.actorUsername || '').toLowerCase().includes(q));
    }
    if (filterAction !== 'all') result = result.filter(e => e.action === filterAction);
    return result;
  }, [entries, searchTerm, filterAction]);

  const formatTimestamp = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const formatContext = (context?: string): string => {
    if (!context) return '';
    try {
      const parsed = JSON.parse(context);
      const parts: string[] = [];
      if (parsed.role) parts.push(`role: ${parsed.role}`);
      if (parsed.note) parts.push(`note: "${parsed.note}"`);
      if (parsed.fromStatus && parsed.toStatus) parts.push(`${parsed.fromStatus} → ${parsed.toStatus}`);
      return parts.join(' · ');
    } catch {
      return context;
    }
  };

  if (loading) {
    return (
      <div className={styles.fleetContainer}>
        <div className={styles.loading}><p>Loading audit log...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.fleetContainer}>
        <div className={styles.emptyState}>
          <FileText size={64} />
          <h3>Cannot load audit log</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const actionOptions = Object.keys(ACTION_LABELS);

  return (
    <div className={styles.fleetContainer}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <FileText className={styles.titleIcon} /> Org Fleet Audit Log
          </h1>
          <p className={styles.subtitle}>
            Last 1,000 pledge events. Older entries are automatically pruned as new ones land.
          </p>
        </div>
        <div className={styles.headerButtons}>
          <Link to="/leader-dashboard/org-fleet" className={styles.guideButton}>
            <Flag size={16} /> Back to Org Fleet
          </Link>
          <Link to="/leader-dashboard" className={styles.guideButton}>
            <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} /> Dashboard
          </Link>
        </div>
      </div>

      <div className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search ship, manufacturer, or user..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value="all">All Actions ({entries.length})</option>
            {actionOptions.map(action => (
              <option key={action} value={action}>{ACTION_LABELS[action] || action}</option>
            ))}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <FileText size={64} />
          <h3>{entries.length === 0 ? 'No audit entries yet' : 'No entries match these filters'}</h3>
          <p>
            {entries.length === 0
              ? 'Pledge events from members will appear here as they happen.'
              : 'Try clearing your filters.'}
          </p>
        </div>
      ) : (
        <div className={styles.shipTable}>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Action</th>
                <th>Ship</th>
                <th>Manufacturer</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id}>
                  <td>{formatTimestamp(e.timestamp)}</td>
                  <td>{e.actorUsername || `user#${e.actorUserId}`}</td>
                  <td>{ACTION_LABELS[e.action] || e.action}</td>
                  <td><strong>{e.shipName}</strong></td>
                  <td>{e.manufacturer}</td>
                  <td className={styles.orgFleetAuditContext}>{formatContext(e.context)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrgFleetAudit;
